import { useTranslation } from "react-i18next";
import { Manager } from "../../../Manager";
import { fetchBanners, type Banner } from "../../../../state/banners";
import { supabase } from "../../../../lib/supabase";
import type { SearchParams, SortParams } from "../../../Manager/types";
import { useCallback } from "react";
import { Box } from "@radix-ui/themes";

const SEARCH_COLUMNS = ["name"];

const BANNER_TYPE_OPTIONS = [
  { value: "restaurant" as const, key: "banners.typeRestaurant" },
  { value: "grocery_store" as const, key: "banners.typeGroceryStore" },
  { value: "liquor_store" as const, key: "banners.typeLiquorStore" },
];

export function ManageBanners() {
  const { t } = useTranslation();

  const fetchData = useCallback(
    (
      limit?: number,
      offset?: number,
      searchParams?: SearchParams,
      sortParams?: SortParams,
    ) => fetchBanners(limit, offset, searchParams, sortParams),
    [],
  );

  const handleSubmit = async (
    data: Record<string, unknown>,
    item: Banner | null,
  ) => {
    const bannerData = {
      name: data.name as string,
      type: data.type as Banner["type"],
    };

    if (item) {
      // Update
      const { error } = await supabase
        .from("banners")
        .update(bannerData)
        .eq("id", item.id);
      if (error) throw new Error(error.message);
    } else {
      // Create
      const { error } = await supabase.from("banners").insert([bannerData]);
      if (error) throw new Error(error.message);
    }
  };

  const handleDelete = async (item: Banner) => {
    const { error } = await supabase.from("banners").delete().eq("id", item.id);
    if (error) throw new Error(error.message);
  };

  return (
    <Box p="4">
      <Manager.Root<Banner> title={t("banners.title")} fetchData={fetchData}>
        <Manager.Search>
          <Manager.TextSearch
            columns={SEARCH_COLUMNS}
            placeholder={t("banners.searchPlaceholder")}
          />
        </Manager.Search>

        <Manager.CreateButton label={t("banners.createBanner")} />

        <Manager.Table>
          <Manager.Column<Banner>
            header={t("banners.name")}
            accessor={(b) => b.name}
          />
          <Manager.Column<Banner>
            header={t("banners.type")}
            accessor={(b) => {
              const opt = BANNER_TYPE_OPTIONS.find((o) => o.value === b.type);
              return opt ? t(opt.key) : b.type;
            }}
          />
          <Manager.Actions>
            <Manager.EditAction />
            <Manager.DeleteAction />
          </Manager.Actions>
        </Manager.Table>

        <Manager.FormDialog<Banner>
          title={(isEdit) =>
            isEdit ? t("banners.editBanner") : t("banners.createBanner")
          }
          description={(isEdit) =>
            isEdit
              ? t("banners.editDescription")
              : t("banners.createDescription")
          }
          onSubmit={handleSubmit}
          validate={(data) =>
            (data.name as string)?.trim() !== "" && !!(data.type as string)
          }
        >
          <Manager.TextField
            name="name"
            label={t("banners.name")}
            required
            placeholder={t("banners.namePlaceholder")}
            getValue={(item) => (item as Banner).name}
          />
          <Manager.SelectField<(typeof BANNER_TYPE_OPTIONS)[number]>
            name="type"
            label={t("banners.type")}
            placeholder={t("banners.selectType")}
            options={BANNER_TYPE_OPTIONS}
            getOptionLabel={(o) => t(o.key)}
            getOptionValue={(o) => o.value}
            getValue={(item) => (item as Banner).type}
          />
        </Manager.FormDialog>

        <Manager.DeleteDialog<Banner>
          getItemName={(b) => b.name}
          onDelete={handleDelete}
        />
      </Manager.Root>
    </Box>
  );
}
